/**
 * The hardest prior-art search, read by the client.
 *
 * A conjecture pass proposes what its model recalls no source stating. That
 * recall is the model's report on its own memory, and memory is exactly the
 * thing that cannot know what it never read — so a conjecture gets a search that
 * ASSUMES it has been said before and goes after where. This file reads what
 * that search returned. No model is asked for the reading, and none is given the
 * chance to supply one: the pass's schema has no field for it.
 *
 * The link scan's discipline, carried over (`link.ts`'s header says why): the
 * null is EARNED, and every threshold here leans toward "already stated". A
 * false "stated" costs one candidate. A false "not found" is the app announcing
 * a discovery, which everything else in it is built never to do — and that
 * asymmetry runs the opposite way to `refuted`'s, where the loud reading is the
 * dangerous one and the gate leans away from it.
 *
 * Five steps, in order:
 *  1. The citation gate — `matchSeen`, the source check's own. A page the model
 *     names that the search never returned is deleted unread, and counted.
 *  2. The quote check — `quoteFoundOn`, the source check's own — wherever the
 *     provider returned the page's text.
 *  3. A mechanical backstop over EVERY returned page, listed or not. A page that
 *     carries enough of the leap's distinctive terms AND of the prediction's to
 *     count as addressing both, by the link scan's own floors, is listed as close
 *     by term overlap and rules out `not-found`.
 *  4. The reading, by precedence. Prior art that was found stands whatever else
 *     went wrong; a failure or a cut voids only the null.
 *  5. `measuredNovelty`, down only, and only against a surviving `same` or
 *     `overlapping` page.
 *
 * Nothing in this file returns, stores or prints a statement that a conjecture
 * is new. The strongest thing a search can do is go looking and not find it, and
 * `not-found` says exactly that and nothing more.
 *
 * Pure: no store, no clock, no model. The citation gate and the quote check are
 * imported rather than restated, so this file cannot hold a second copy of
 * either.
 */

import type {
  ConjectureRecord,
  ConjectureRegistration,
  ModelStamp,
  PriorArtBackstopHit,
  PriorArtEntry,
  PriorArtSearch,
  ReasonNode,
} from './types'
import { canonicalUrl, isHttpUrl } from './url'
import { fnv1a64, foldText, quoteFoundOn } from './similarity'
import { ADDRESSES_BOTH_TEXT, ADDRESSES_BOTH_TITLE, distinctiveTerms, termCoverage } from './link'
import { SOURCE_QUOTE_CHARS } from './textRetention'
import { PRIOR_ART_READING_LABEL, PRIOR_ART_STATED_SINCE } from './vocabulary'
import { clamp } from './geometry'
import { calendarDay, calendarYear } from './dates'
import { fmtRating, selfRatingsOf } from './rewrite'
import { failureNote, matchSeen } from '../engine/providers/contract'
import type { SearchOutcome, SeenSource } from '../engine/providers/contract'

/* ── the budget ────────────────────────────────────────────────────────── */

/**
 * Searches one prior-art search may run, and pages it may open.
 *
 * Above the source check's six and three, deliberately: this pass exists to
 * find a statement that may be worded in another field's vocabulary, in an old
 * paper, or as a remark inside a review, and its null is only worth the effort
 * behind it. A bound where the API enforces one and a sentence in the brief
 * where it does not, exactly as `VERIFY_BUDGET` is.
 */
export const PRIOR_ART_MAX_SEARCHES = 8
export const PRIOR_ART_MAX_FETCHES = 4

/* ── the readings ──────────────────────────────────────────────────────── */

/**
 * Every reading, as a VALUE, in precedence order — `persist.ts` validates a
 * stored reading against this list, the reason `LINK_READINGS` is a value.
 *
 *   stated         A returned page takes this conjecture's step, and its quote
 *                  was not shown to be missing from the page.
 *   partly-stated  A returned page states part of it; or one listed as stating
 *                  it whose quote is not on the page; or one the backstop found
 *                  close by term overlap. Each rules out the null.
 *   unsound        A search step failed, or the pass was cut off, and nothing
 *                  above it was found. A null read off that is not a null.
 *   thin           Fewer pages came back than silence needs to mean anything.
 *   not-found      The search ran, came back whole with enough pages, and none
 *                  of them states it. A place to look, never a finding.
 */
export const PRIOR_ART_READINGS = [
  'stated',
  'partly-stated',
  'unsound',
  'thin',
  'not-found',
] as const

export type PriorArtReading = (typeof PRIOR_ART_READINGS)[number]

/**
 * Below this many distinct returned pages, a search's silence means nothing.
 *
 * One page per search the pass may run. The link scan asks four per side of a
 * gather of two to four queries; this is one pass of up to eight, and a pass
 * that surfaced fewer pages than it had searches to spend did not surface a
 * literature. Leans toward `thin`, which is not a null, rather than toward
 * `not-found`, which is the expensive error.
 */
export const PRIOR_ART_MIN_RESULTS = PRIOR_ART_MAX_SEARCHES

/**
 * The share of the leap's distinctive terms, and of the prediction's, a returned
 * page must carry — on EACH side — for the backstop to list it as close.
 *
 * The link scan's floors for a page addressing two subjects at once, which is
 * this question exactly, and the titles floor is lower for the reason that file
 * gives: a title carries a smaller share of any term set than a page would, and
 * every miss that brevity causes lands on `not-found`. Which floor applies is a
 * property of the PAGE and never of the pass.
 *
 * Provisional in one sense and not in another. The planted-control study
 * (`evals/conjectures/PROTOCOL.md`) is where these get chosen for this pass and
 * where how often they fire gets reported; until it has run, the app's one
 * existing definition of "addresses both" is the honest default, and it is not a
 * number chosen here.
 */
export const PRIOR_ART_OVERLAP_MIN = { text: ADDRESSES_BOTH_TEXT, title: ADDRESSES_BOTH_TITLE }

/** Entries kept per search. Sorted closest first before the cut, so a `same`
 *  can never be the entry the display bound drops. */
export const PRIOR_ART_MAX_ENTRIES = 6

/** Backstop hits kept per search. `backstop.close` still counts every one. */
export const PRIOR_ART_MAX_HITS = 6

/** Searches kept per conjecture, oldest dropped first. A later convergence is
 *  read against the ones kept. */
export const PRIOR_ART_HISTORY = 4

/**
 * How far a surviving page caps `measuredNovelty`, by how close the page is.
 *
 * A CEILING, applied with `min` against the conjecture's own rating, so the
 * number can only ever go down. `same` is not zero because a conjecture can add
 * something to the work that already takes its step — the reading's own note
 * says "whatever the conjecture adds" — and `overlapping` caps lower than no
 * page at all but well above `same`, because the page states part of it and
 * not the rest. `adjacent` never moves the number: the same field is not the
 * same claim.
 */
export const PRIOR_ART_NOVELTY_CEILING = { same: 0.1, overlapping: 0.4 } as const

/* ── the backstop ──────────────────────────────────────────────────────── */

/**
 * The terms a returned page is compared on: the leap's, and the prediction's
 * condition, observable and expected outcome.
 *
 * Not `consensusExpects`, and not `departsFrom`. Those are what the consensus
 * holds, and a page carrying the consensus's vocabulary is a page about the
 * consensus — counting it as close to the conjecture would have the backstop
 * fire on the very literature the conjecture departs from.
 */
export function priorArtTerms(c: Pick<ConjectureRecord, 'leap' | 'prediction'>): {
  leap: string[]
  prediction: string[]
} {
  const p = c.prediction
  return {
    leap: distinctiveTerms({ label: '', claim: c.leap }),
    prediction: distinctiveTerms({
      label: '',
      claim: `${p.condition} ${p.observable} ${p.expected}`,
    }),
  }
}

/** The distinct pages a search returned, first occurrence kept. */
function distinctPages(pages: readonly SeenSource[]): SeenSource[] {
  const seen = new Set<string>()
  const out: SeenSource[] = []
  for (const p of pages) {
    const key = canonicalUrl(p.url)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(p)
  }
  return out
}

const share = (x: number): number => Math.round(x * 100) / 100

/**
 * Every returned page, compared against the leap and the prediction.
 *
 * A page is close when it carries at least the floor on BOTH sides: the step
 * and the observation, not either alone. A side with no distinctive terms cannot
 * be read, and a page is never close on a side nobody could compare — that is
 * the one place this leans away from "stated", and only because an empty term
 * set would otherwise make every page close.
 */
export function backstop(
  pages: readonly SeenSource[],
  c: Pick<ConjectureRecord, 'leap' | 'prediction'>,
): { pages: number; close: number; hits: PriorArtBackstopHit[] } {
  const terms = priorArtTerms(c)
  const distinct = distinctPages(pages)
  const hits: PriorArtBackstopHit[] = []
  if (terms.leap.length && terms.prediction.length) {
    for (const p of distinct) {
      const body = p.text ?? p.title
      const floor = p.text !== undefined ? PRIOR_ART_OVERLAP_MIN.text : PRIOR_ART_OVERLAP_MIN.title
      const leap = termCoverage(body, terms.leap)
      const prediction = termCoverage(body, terms.prediction)
      if (leap < floor || prediction < floor) continue
      hits.push({
        url: p.url,
        title: p.title,
        via: p.via,
        leap: share(leap),
        prediction: share(prediction),
      })
    }
  }
  return { pages: distinct.length, close: hits.length, hits: hits.slice(0, PRIOR_ART_MAX_HITS) }
}

/* ── the reading ───────────────────────────────────────────────────────── */

/**
 * The reading, from the parts of a search that can be re-derived from its record.
 *
 * ONE function, read live by `assessPriorArt` and on the way off disk by
 * `regatePriorArt`, so a stored document that carries a `same` page under
 * `not-found` comes back `stated` — the reason `refutedIsEarned` is one function.
 *
 * The order is the argument. Found prior art first, because it is positive
 * evidence and says so whatever else went wrong; `unsound` directly beneath it,
 * because every reading below is a null and a null inherits every doubt the pass
 * collected; then `thin`; and `not-found` only when nothing else applies.
 */
export function priorArtReading(p: {
  entries: readonly Pick<PriorArtEntry, 'closeness' | 'quoteFound'>[]
  close: number
  sound: boolean
  seen: number
}): PriorArtReading {
  if (p.entries.some((e) => e.closeness === 'same' && e.quoteFound !== false)) return 'stated'
  // A page listed as stating any of it rules out the null, even where its quote
  // is not on the page: the quote failing is not the page failing, and the error
  // this file will not make is the silent one.
  if (p.entries.some((e) => e.closeness !== 'adjacent') || p.close > 0) return 'partly-stated'
  if (!p.sound) return 'unsound'
  if (p.seen < PRIOR_ART_MIN_RESULTS) return 'thin'
  return 'not-found'
}

/**
 * Novelty re-read against what survived, DOWN only.
 *
 * Written only against a `same` or `overlapping` page whose quote was not shown
 * to be missing from it, and never above the conjecture's own rating. A search
 * that found nothing close produces no number at all: an absence is not a
 * measurement, and printing one under a field called "measured" would be the
 * tool declaring it has found something.
 */
export function measuredNoveltyOf(
  entries: readonly Pick<PriorArtEntry, 'closeness' | 'quoteFound'>[],
  novelty: number,
): number | undefined {
  let ceiling: number | undefined
  for (const e of entries) {
    if (e.closeness === 'adjacent' || e.quoteFound === false) continue
    const c = PRIOR_ART_NOVELTY_CEILING[e.closeness]
    ceiling = ceiling === undefined ? c : Math.min(ceiling, c)
  }
  return ceiling === undefined ? undefined : Math.min(clamp(novelty), ceiling)
}

/* ── one search, read ──────────────────────────────────────────────────── */

const CLOSENESS = new Set<string>(['same', 'overlapping', 'adjacent'])
const RANK: Record<PriorArtEntry['closeness'], number> = { same: 0, overlapping: 1, adjacent: 2 }

const str = (v: unknown, max: number): string =>
  typeof v === 'string' ? v.trim().slice(0, max) : ''

/** A year a page states, or nothing. Never guessed, never repaired. */
const yearOf = (v: unknown, now: number): number | undefined => {
  if (typeof v !== 'number' || !Number.isInteger(v)) return undefined
  const latest = calendarYear(now) + 1
  return v >= 1000 && v <= latest ? v : undefined
}

/**
 * The fingerprint of the instructions a search ran under. Exact bytes, never
 * folded: a changed prompt is a different instrument, and recall measured on one
 * says nothing about the other.
 */
export const priorArtInstrument = (system: string): string => fnv1a64(system)

export interface PriorArtInput {
  /** The `priorArt` objects the response streamed, as the model wrote them. */
  raw: readonly unknown[]
  /** The response's `summary`, as the model wrote it. */
  summary: unknown
  /** What the search did. `undefined` means nothing was searched at all. */
  search: SearchOutcome | undefined
  /** The response stopped at its output cap, so entries after the cut are lost. */
  truncated: boolean
  /** The conjecture searched. */
  node: Pick<ReasonNode, 'novelty' | 'conjecture'>
  model: ModelStamp
  instrument: string
  now: number
}

/**
 * One response → the record the app keeps.
 *
 * EXPORTED FOR THE EVAL SEAM as well as for `reason.ts`: the planted-control
 * harness reads its searches through this function, because a harness carrying
 * its own copy of the rules would measure its copy.
 */
export function assessPriorArt(input: PriorArtInput): PriorArtSearch {
  const c = input.node.conjecture
  if (!c) throw new Error('A prior-art search reads against an insight, and this node is not one.')
  const pages = input.search?.sources ?? []

  const kept: PriorArtEntry[] = []
  const keptKeys = new Set<string>()
  let dropped = 0
  for (const item of input.raw) {
    const o = (item ?? {}) as Record<string, unknown>
    const url = str(o.url, 2000)
    if (!url || !isHttpUrl(url)) {
      dropped++
      continue
    }
    const hit = matchSeen(url, pages)
    // THE gate. A page the search never returned is not prior art, whatever the
    // model calls it — and the count is surfaced, because a silent drop is
    // indistinguishable from a search that had nothing to report.
    if (!hit) {
      dropped++
      continue
    }
    const key = canonicalUrl(hit.url)
    if (keptKeys.has(key)) continue
    keptKeys.add(key)
    const proposedQuote = typeof o.quote === 'string' ? o.quote.trim() : ''
    const page = hit.via === 'fetch' && hit.text !== undefined ? foldText(hit.text) : undefined
    const quoteFound = quoteFoundOn(page, proposedQuote, hit.truncated)
    const closeness = CLOSENESS.has(str(o.closeness, 20))
      ? (str(o.closeness, 20) as PriorArtEntry['closeness'])
      : 'adjacent'
    const year = yearOf(o.year, input.now)
    kept.push({
      url: hit.url,
      // Never the model's. The title is what carries a source's authority.
      title: hit.title,
      quote: proposedQuote.slice(0, SOURCE_QUOTE_CHARS),
      closeness,
      covers: str(o.covers, 400),
      ...(year !== undefined ? { year } : {}),
      via: hit.via,
      ...(quoteFound !== undefined ? { quoteFound } : {}),
    })
  }
  // Closest first, stable within a closeness, then the display bound — so the
  // bound can drop an adjacent page and never a `same` one.
  const entries = kept
    .map((e, i) => ({ e, i }))
    .sort((a, b) => RANK[a.e.closeness] - RANK[b.e.closeness] || a.i - b.i)
    .map((x) => x.e)
    .slice(0, PRIOR_ART_MAX_ENTRIES)

  const found = backstop(pages, c)
  const failures = [
    // Recorded as a failure, not only reasoned as one below: a stored record is
    // re-read from its fields, and this is the field that keeps a pass that
    // searched nothing from coming back as a search that found little.
    ...(input.search ? [] : ['Nothing was searched.']),
    ...(input.search?.failures ?? []).map((f) => failureNote(f.code)),
    ...(input.search?.exhausted ? ['The search was cut off before it finished.'] : []),
    ...(input.truncated
      ? ['The response stopped at its length cap, so anything listed after the cut was lost.']
      : []),
  ]
  const partial = !!input.search?.exhausted || input.truncated
  // No search at all is the least sound pass there is: nothing was looked for.
  const sound = !!input.search && !input.search.failures.length && !partial
  const reading = priorArtReading({ entries, close: found.close, sound, seen: found.pages })
  const measuredNovelty = measuredNoveltyOf(entries, input.node.novelty)
  const summary = str(input.summary, 600)

  return {
    reading,
    entries,
    backstop: found,
    ...(summary ? { summary } : {}),
    queries: [...(input.search?.queries ?? [])],
    seen: found.pages,
    dropped,
    ...(failures.length ? { failures: [...new Set(failures)] } : {}),
    ...(partial ? { partial: true } : {}),
    ...(measuredNovelty !== undefined ? { measuredNovelty } : {}),
    ...(input.search ? { quotesCheckable: input.search.pageText } : {}),
    searchedAt: input.now,
    model: input.model,
    contentHash: c.registered.contentHash,
    instrument: input.instrument,
  }
}

/**
 * A stored search, re-read: its reading and `measuredNovelty` re-derived from its
 * own entries and counts, never trusted as written.
 *
 * `persist.ts` calls this on the way off disk, so a document that says
 * `not-found` over a `same` page, or carries a number above the conjecture's own
 * rating, comes back as what its evidence says. A stricter read, and so no
 * `SCHEMA` bump — the reason `asVerification` needs none for `refuted`.
 */
export function regatePriorArt(s: PriorArtSearch, novelty: number): PriorArtSearch {
  const sound = !s.failures?.length && !s.partial
  const reading = priorArtReading({
    entries: s.entries,
    close: s.backstop.close,
    sound,
    seen: s.seen,
  })
  const measuredNovelty = measuredNoveltyOf(s.entries, novelty)
  const { measuredNovelty: _stored, ...rest } = s
  void _stored
  return {
    ...rest,
    reading,
    ...(measuredNovelty !== undefined ? { measuredNovelty } : {}),
  }
}

/* ── reading a conjecture's searches ───────────────────────────────────── */

/** The newest search run against a conjecture, or undefined. */
export const latestPriorArt = (n: Pick<ReasonNode, 'conjecture'>): PriorArtSearch | undefined =>
  n.conjecture?.priorArt?.at(-1)

/** Whether a search was of the wording the conjecture carries now. */
export function priorArtLive(n: Pick<ReasonNode, 'conjecture'>, s: PriorArtSearch): boolean {
  return !!n.conjecture && s.contentHash === n.conjecture.registered.contentHash
}

/** The readings of a search that completed and read something outside the
 *  model. `thin` and `unsound` looked and could not say; they lift nothing. */
const SEARCHED: ReadonlySet<PriorArtReading> = new Set(['stated', 'partly-stated', 'not-found'])

/**
 * Whether a conjecture has had outside contact through a prior-art search.
 *
 * Contact, not agreement: a search that found it stated and one that did not
 * find it both went out of the building, which is what the spatial view's
 * height means. Only a search of the current wording counts.
 */
export function priorArtSearched(n: Pick<ReasonNode, 'conjecture'>): boolean {
  const s = latestPriorArt(n)
  return !!s && priorArtLive(n, s) && SEARCHED.has(s.reading)
}

/* ── later convergence ─────────────────────────────────────────────────── */

/** The registration a search read — the one whose fingerprint it names — or
 *  undefined when a rewrite has since dropped it from the history. */
export function searchedRegistration(
  c: Pick<ConjectureRecord, 'registered' | 'registrations'>,
  s: Pick<PriorArtSearch, 'contentHash'>,
): ConjectureRegistration | undefined {
  return [c.registered, ...(c.registrations ?? [])].find((r) => r.contentHash === s.contentHash)
}

/**
 * The pages a search found taking this conjecture's step that date themselves
 * AFTER the wording it searched was registered: stated elsewhere later, not
 * before (D282). The literature's side of independent convergence, and a record
 * rather than a verdict — on the conjecture or on the page.
 *
 * Only a surviving `same` page counts: an `overlapping` one states part of it,
 * and a page whose quote is not on it states nothing this app could read. Only a
 * page stating its own year, because the model is told never to guess one; and
 * only a year strictly after the registration's, because a page dated the same
 * year cannot be put before it or after it. A page's date is the page's own
 * claim, and this says so wherever it prints.
 */
export function laterStatements(
  c: Pick<ConjectureRecord, 'registered' | 'registrations'>,
  s: PriorArtSearch,
): PriorArtEntry[] {
  const reg = searchedRegistration(c, s)
  if (!reg) return []
  // The reader's year, the one the card prints the registration's day in: the
  // UTC year disagreed with that day for hours around every New Year.
  const year = calendarYear(reg.at)
  return s.entries.filter(
    (e) =>
      e.closeness === 'same' && e.quoteFound !== false && e.year !== undefined && e.year > year,
  )
}

/** A record with one more search on it, the oldest dropped past the history. */
export function withPriorArt(c: ConjectureRecord, s: PriorArtSearch): ConjectureRecord {
  return { ...c, priorArt: [...(c.priorArt ?? []), s].slice(-PRIOR_ART_HISTORY) }
}

/**
 * The re-read rating, as one sentence: the proposing model's own figure, and the
 * ceiling a surviving page put on it. Null when nothing close survived — no page,
 * no number.
 *
 * The model's own figure only where it rated these words (`selfRatingsOf`, D204),
 * printed through `fmtRating` (D263). A conjecture the reader rewrote keeps a
 * number that rated the wording it replaced, so the sentence drops the
 * self-rated clause for it, as the digest's prior-art line does (D285) — and it
 * names the ceiling alone, since `measuredNovelty` is that ceiling or the old
 * rating, whichever is lower, and the lower one could be the old rating printed
 * under another name.
 */
export function measuredSentence(
  n: Pick<ReasonNode, 'editedAt' | 'carriedFrom' | 'confidence' | 'novelty' | 'tension'>,
  s: Pick<PriorArtSearch, 'measuredNovelty' | 'entries'>,
): string | null {
  if (s.measuredNovelty === undefined) return null
  const tail = 'A surviving page can only lower that figure, and nothing here raises it.'
  const own = selfRatingsOf(n)
  if (!own) {
    // The ceiling the pages put on any rating at all: the conjecture's own, `min`'d
    // in, is the part that rated other words.
    const cap = measuredNoveltyOf(s.entries, 1) ?? s.measuredNovelty
    return `The proposing model's self-rated distance from the obvious rated the wording this replaced, so it is not printed; against the page above, any figure for this wording is capped at ${fmtRating(cap)}. ${tail}`
  }
  return `Its self-rated distance from the obvious was ${fmtRating(own.novelty)}; against the page above it is capped at ${fmtRating(s.measuredNovelty)}. ${tail}`
}

/* ── what a search's recall is, and is not, known to be ────────────────── */

/**
 * One run of the planted controls: ideas already in the literature under other
 * vocabulary, searched as though each were a conjecture.
 *
 * Keyed by model AND instrument. Recall measured under one set of instructions
 * says nothing about another, and a search whose instructions have changed since
 * the controls ran is a search whose recall has not been measured.
 */
export interface PriorArtRecall {
  model: { provider: ModelStamp['provider']; id: string; name: string }
  instrument: string
  /** The date the controls ran, `YYYY-MM-DD`. */
  on: string
  controls: number
  /** Controls the search came back `stated` or `partly-stated` on. */
  found: number
}

/**
 * Measured recall, by model and instrument. EMPTY until the owner runs the
 * planted-control study and accepts its row in a reviewed commit: a disclosure
 * that changes what the product says about its own null is a code change, and
 * `evals/conjectures/controls.eval.ts` prints the row to add rather than writing
 * it here itself.
 */
export const PRIOR_ART_RECALL: readonly PriorArtRecall[] = []

/**
 * A Wilson score interval for `k` of `n`, at 95% by default. Chosen over the
 * normal approximation because the counts here are twenty-odd and the rates sit
 * near one, where the normal interval runs past its own bounds.
 */
export function wilsonInterval(k: number, n: number, z = 1.96): { lo: number; hi: number } {
  if (n <= 0) return { lo: 0, hi: 1 }
  const p = k / n
  const z2 = z * z
  const centre = (p + z2 / (2 * n)) / (1 + z2 / n)
  const half = (z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / (1 + z2 / n)
  return { lo: Math.max(0, centre - half), hi: Math.min(1, centre + half) }
}

/** The recall row for a search, when its model and instrument have one. */
export function recallFor(
  s: Pick<PriorArtSearch, 'model' | 'instrument'>,
  table: readonly PriorArtRecall[] = PRIOR_ART_RECALL,
): PriorArtRecall | undefined {
  return table
    .filter(
      (r) =>
        r.model.provider === s.model.provider &&
        r.model.id === s.model.id &&
        r.instrument === s.instrument,
    )
    .sort((a, b) => b.on.localeCompare(a.on))[0]
}

const pct = (x: number): string => `${Math.round(x * 100)}%`

/**
 * What the null is worth, said beside it: the planted-control result for this
 * search's model and instructions, or that there is none.
 */
export function recallSentence(
  s: Pick<PriorArtSearch, 'model' | 'instrument'>,
  table: readonly PriorArtRecall[] = PRIOR_ART_RECALL,
): string {
  const r = recallFor(s, table)
  if (!r) return "This search's recall has not been measured."
  const { lo, hi } = wilsonInterval(r.found, r.controls)
  return `On ${r.controls} ideas already in the literature, this search found ${r.found} stated or partly stated (${r.model.name}, ${r.on}; 95% interval ${pct(lo)}–${pct(hi)}).`
}

/* ── the words ─────────────────────────────────────────────────────────── */

// Each reading's label is a status word, so `PRIOR_ART_READING_LABEL` is defined
// in core/vocabulary.ts, with every other one, and re-exported from here for the
// surfaces that read the rest of the search's words from this file.
export { PRIOR_ART_READING_LABEL }

/**
 * The note under a reading, from the record alone.
 *
 * `partly-stated` has three ways to be earned and says which: a page that states
 * part of it, a page listed as stating it whose quote is not on the page, or a
 * page only the backstop found. Folding them into one sentence would describe a
 * term count as a reading.
 */
/** Whether every page a search found taking the step dates itself after the
 *  registration it searched — so "before" would be false of all of them. */
const onlyLater = (s: PriorArtSearch, later: readonly PriorArtEntry[]): boolean =>
  later.length > 0 &&
  later.length >= s.entries.filter((e) => e.closeness === 'same' && e.quoteFound !== false).length

/**
 * A search's reading as a label, from the record and its later statements.
 *
 * `stated` reads "Stated before" everywhere except where every page taking the
 * step dates itself after the conjecture was registered — then "Stated elsewhere
 * since", because "before" would be false of every page it rests on. The reading
 * itself does not change: the step has been stated, and the ceiling on the
 * re-read rating holds either way.
 */
export function priorArtLabel(s: PriorArtSearch, later: readonly PriorArtEntry[] = []): string {
  return s.reading === 'stated' && onlyLater(s, later)
    ? PRIOR_ART_STATED_SINCE
    : PRIOR_ART_READING_LABEL[s.reading]
}

export function priorArtNote(
  s: PriorArtSearch,
  table: readonly PriorArtRecall[] = PRIOR_ART_RECALL,
  /** `laterStatements` for this search, when the caller has the conjecture. A
   *  page dated after the registration is a later statement, and "before you"
   *  would be false of it. */
  later: readonly PriorArtEntry[] = [],
): string {
  switch (s.reading) {
    case 'stated': {
      if (onlyLater(s, later)) {
        return `A page this search returned takes this step, and dates itself after this insight was registered — stated elsewhere later, not before. That is a record of a later statement, not a verdict on either; a page's date is the page's own claim.`
      }
      const k = later.length
      return `A page this search returned already takes this step. Whatever the insight adds, somebody has been here before you.${
        k
          ? ` ${k} of the pages taking it ${k === 1 ? 'dates itself' : 'date themselves'} after this was registered, by ${k === 1 ? 'its' : 'their'} own claim.`
          : ''
      }`
    }
    case 'partly-stated': {
      if (s.entries.some((e) => e.closeness === 'overlapping' && e.quoteFound !== false)) {
        return 'A page this search returned states part of this — the mechanism without the prediction, or the prediction for another reason. Read it before building on the rest.'
      }
      if (s.entries.some((e) => e.closeness !== 'adjacent')) {
        return 'The search listed a page as stating this, and the sentence it quoted is not on that page. A quote failing is not the page failing: read the page before building on the rest.'
      }
      const k = s.backstop.close
      return `No page was listed as stating this, but ${k} returned page${k === 1 ? ' carries' : 's carry'} enough of its leap's terms and its prediction's to address both. That is term overlap, not a reading — open ${k === 1 ? 'it' : 'them'} before building on the rest.`
    }
    case 'unsound':
      return 'A search failed or was cut off, so nothing can be read from what it did not return. Run it again.'
    case 'thin':
      return `The search returned ${s.seen} page${s.seen === 1 ? '' : 's'}, too few for their silence to mean anything.`
    case 'not-found': {
      const n = s.queries.length
      return `A search that went looking for this insight came back without it, on ${calendarDay(s.searchedAt)}, after ${n} quer${n === 1 ? 'y' : 'ies'}. That is a place to look, not a finding: a search finding nothing is a search finding nothing. ${recallSentence(s, table)}`
    }
  }
}
